import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * A single visit in the application's logical navigation history.
 *
 * The visit ID identifies the occurrence, while `path` identifies the route.
 * This allows the same route to occur more than once in a chain.
 */
export interface StateChainItem {
    /** Stable identifier for this visit. */
    id: string;
    /** Router state or route name. */
    name: string;
    /** Untranslated page title supplied by the route. */
    pageTitle: string;
    /** Relative application URL, excluding the hash prefix. */
    path: string;
    /** Resolved title displayed in breadcrumbs and tabs. */
    title: string;
    /** Router-specific state metadata retained for compatibility. */
    state: unknown;
    /** Parameters used for this particular visit. */
    parms: { [key: string]: unknown };
    /** Creation timestamp retained for legacy storage compatibility. */
    date: string;
    /** Position assigned to the matching native browser-history entry. */
    browserPosition?: number;
    /** Whether the adapter recorded this visit automatically at startup. */
    isStartup?: boolean;
}

/** Serializable state used to restore a navigation chain in the same tab. */
export interface NavigationHistorySnapshot {
    entries: StateChainItem[];
    activeIndex: number;
}

/** Router-adapter metadata supplied when a navigation completes. */
export interface RecordNavigationOptions {
    browserPosition?: number;
    replace?: boolean;
    visitId?: string;
}

export type StateChainItemInput = Omit<StateChainItem, 'id'> & Partial<Pick<StateChainItem, 'id'>>;

/**
 * Owns the application's router-independent navigation history.
 *
 * Router adapters report completed navigations to this service. The service
 * maintains the ordered visits and active cursor, but never initiates route
 * changes itself. UI-Router can therefore be replaced without changing the
 * breadcrumb or pin-history rules.
 *
 * `stateChain$` contains the chain through the active cursor. The complete
 * chain, including entries available through Forward, is exposed by `snapshot`.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
    /** Breadcrumb entries through the currently active visit. */
    public readonly stateChain$ = new BehaviorSubject<StateChainItem[]>([]);
    /** The visit at the active cursor, or `null` for an empty chain. */
    public readonly activeState$ = new BehaviorSubject<StateChainItem | null>(null);

    private entries: StateChainItem[] = [];
    private activeIndex = -1;
    private protectedPaths = new Set<string>();
    private idSequence = 0;

    /** Returns a defensive copy suitable for session persistence. */
    public get snapshot(): NavigationHistorySnapshot {
        return {
            entries: this.entries.map((entry) => ({ ...entry })),
            activeIndex: this.activeIndex,
        };
    }

    /**
     * Restores a persisted chain and clamps an invalid cursor.
     * Legacy entries without visit IDs receive IDs during hydration.
     */
    public hydrate(snapshot: Partial<NavigationHistorySnapshot> | null | undefined): void {
        const entries = snapshot?.entries ?? [];

        this.entries = entries.map((entry) => ({
            ...entry,
            id: entry.id || entry.date || this.createId(),
            date: entry.date || new Date().toISOString(),
        }));

        const requestedIndex = Number(snapshot?.activeIndex);
        this.activeIndex =
            this.entries.length === 0
                ? -1
                : Number.isInteger(requestedIndex)
                  ? Math.min(Math.max(requestedIndex, 0), this.entries.length - 1)
                  : this.entries.length - 1;

        this.publish();
    }

    /** Assigns native positions when a copied trail is recreated in another tab. */
    public rebaseBrowserPositions(): void {
        this.entries = this.entries.map((entry, index) => ({
            ...entry,
            browserPosition: index <= this.activeIndex ? index : undefined,
        }));
        this.publish();
    }

    /** Activates a breadcrumb and removes every later visit from its trail. */
    public truncateAfter(id: string): StateChainItem | null {
        const targetIndex = this.entries.findIndex((entry) => entry.id === id);
        if (targetIndex < 0) return null;

        this.entries = this.entries.slice(0, targetIndex + 1);
        this.activeIndex = targetIndex;
        this.publish();
        return this.entries[targetIndex];
    }

    /** Replaces the URLs that must survive forward-branch compaction. */
    public setProtectedPaths(paths: Iterable<string>): void {
        this.protectedPaths = new Set(Array.from(paths).filter(Boolean));
    }

    /** Updates one protected URL for the legacy breadcrumb compatibility API. */
    public setPathProtected(path: string, protectedState: boolean): void {
        if (protectedState) this.protectedPaths.add(path);
        else this.protectedPaths.delete(path);
    }

    /**
     * Removes pinned routes recorded only because they were the initial URL.
     * A later explicit pin click records a normal visit.
     */
    public removeUnvisitedStartupEntries(): void {
        const activeId = this.entries[this.activeIndex]?.id;
        this.entries = this.entries.filter((entry) => !entry.isStartup || !this.protectedPaths.has(entry.path));
        this.activeIndex = activeId ? this.entries.findIndex((entry) => entry.id === activeId) : -1;
        if (this.activeIndex < 0 && this.entries.length > 0) this.activeIndex = this.entries.length - 1;
        this.publish();
    }

    /**
     * Reconciles a completed router transition with logical history.
     *
     * Known visit IDs move the cursor for browser Back/Forward. Refreshes reuse
     * the active visit. New navigation after Back drops ordinary forward entries
     * but retains one checkpoint for each protected URL.
     */
    public recordNavigation(
        entry: StateChainItemInput,
        options: RecordNavigationOptions = {},
    ): StateChainItem {
        if (options.visitId) {
            const existingIndex = this.entries.findIndex((item) => item.id === options.visitId);

            if (existingIndex >= 0 && this.isSameDestination(this.entries[existingIndex], entry)) {
                this.entries[existingIndex] = {
                    ...this.entries[existingIndex],
                    ...entry,
                    id: this.entries[existingIndex].id,
                    browserPosition: options.browserPosition ?? this.entries[existingIndex].browserPosition,
                };
                this.activeIndex = existingIndex;
                this.publish();
                return this.entries[existingIndex];
            }
        }

        const activeEntry = this.entries[this.activeIndex];

        if (entry.name === 'mainmenu') {
            const mainMenuEntry = this.createEntry(entry, options);
            this.entries = [mainMenuEntry];
            this.activeIndex = 0;
            this.publish();
            return mainMenuEntry;
        }

        if (activeEntry && this.isSameDestination(activeEntry, entry)) {
            this.entries[this.activeIndex] = {
                ...activeEntry,
                ...entry,
                id: activeEntry.id,
                date: activeEntry.date,
                browserPosition: options.browserPosition ?? activeEntry.browserPosition,
            };
            this.publish();
            return this.entries[this.activeIndex];
        }

        if (options.replace && activeEntry) {
            this.entries[this.activeIndex] = {
                ...this.createEntry(entry, options),
                id: activeEntry.id,
                date: activeEntry.date,
            };
            this.publish();
            return this.entries[this.activeIndex];
        }

        if (this.activeIndex < this.entries.length - 1) {
            const prefix = this.entries.slice(0, this.activeIndex + 1);
            const retainedPaths = new Set(
                prefix
                    .filter((item) => this.protectedPaths.has(item.path))
                    .map((item) => item.path),
            );
            const protectedForwardEntries = this.entries.slice(this.activeIndex + 1).filter((item) => {
                if (!this.protectedPaths.has(item.path) || retainedPaths.has(item.path)) return false;

                retainedPaths.add(item.path);
                return true;
            });

            this.entries = [
                ...prefix,
                ...protectedForwardEntries.map((item) => ({
                    ...item,
                    browserPosition: undefined,
                })),
            ];
        }

        const newEntry = this.createEntry(entry, options);
        this.entries.push(newEntry);
        this.activeIndex = this.entries.length - 1;
        this.publish();
        return newEntry;
    }

    /** Moves the cursor to a known visit without deleting entries. */
    public activate(id: string, browserPosition?: number): StateChainItem | null {
        const targetIndex = this.entries.findIndex((entry) => entry.id === id);
        if (targetIndex < 0) return null;

        this.activeIndex = targetIndex;
        if (browserPosition !== undefined) {
            this.entries[targetIndex] = {
                ...this.entries[targetIndex],
                browserPosition,
            };
        }

        this.publish();
        return this.entries[targetIndex];
    }

    /** Resolves a previous visit by step count or URL. */
    public getBackTarget(to: number | string = 1): StateChainItem | null {
        if (typeof to === 'string') {
            for (let i = this.activeIndex - 1; i >= 0; i--) {
                if (this.entries[i].path === to) return this.entries[i];
            }
            return null;
        }

        const steps = Number.isFinite(to) ? Math.max(1, Math.trunc(to)) : 1;
        return this.entries[this.activeIndex - steps] ?? null;
    }

    /** Finds a visit by its stable ID. */
    public getEntry(id: string): StateChainItem | null {
        return this.entries.find((entry) => entry.id === id) ?? null;
    }

    /**
     * Calculates the native `history.go()` delta when positions are known.
     * Returns `null` when the adapter must fall back to URL navigation.
     */
    public getBrowserDelta(target: StateChainItem, currentBrowserPosition: unknown): number | null {
        if (typeof target.browserPosition !== 'number' || typeof currentBrowserPosition !== 'number') {
            return null;
        }

        const delta = target.browserPosition - currentBrowserPosition;
        return delta === 0 ? null : delta;
    }

    /** Removes a dismissible, non-active breadcrumb occurrence. */
    public remove(id: string): void {
        const removeIndex = this.entries.findIndex((entry) => entry.id === id);
        if (removeIndex < 0 || removeIndex === this.activeIndex) return;

        this.entries.splice(removeIndex, 1);
        if (removeIndex < this.activeIndex) this.activeIndex--;
        this.publish();
    }

    /** Updates the resolved title of the active visit. */
    public updateActiveTitle(title: string): void {
        if (!title || this.activeIndex < 0 || !this.entries[this.activeIndex]) return;

        this.entries[this.activeIndex] = { ...this.entries[this.activeIndex], title };
        this.publish();
    }

    private createEntry(entry: StateChainItemInput, options: RecordNavigationOptions): StateChainItem {
        return {
            ...entry,
            id: options.visitId || entry.id || this.createId(),
            date: entry.date || new Date().toISOString(),
            browserPosition: options.browserPosition ?? entry.browserPosition,
        };
    }

    private createId(): string {
        this.idSequence++;
        return `navigation-${Date.now()}-${this.idSequence}`;
    }

    private isSameDestination(left: Pick<StateChainItem, 'path' | 'parms'>, right: Pick<StateChainItem, 'path' | 'parms'>): boolean {
        return left.path === right.path && JSON.stringify(left.parms) === JSON.stringify(right.parms);
    }

    private publish(): void {
        const visibleEntries = this.activeIndex < 0 ? [] : this.entries.slice(0, this.activeIndex + 1);
        this.stateChain$.next(visibleEntries);
        this.activeState$.next(this.entries[this.activeIndex] ?? null);
    }
}
